import React, { useState, useCallback } from 'react';
import { Box, Button, Typography, Paper, Container, Card, CardContent, Fade, Grow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const WelcomeAfterRegistration = () => {
    const { t } = useTranslation();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [showEmailForm, setShowEmailForm] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const contactEmail = 'lisafraunhofer@outlook.de';

    const handleEmailClick = () => {
        setShowEmailForm(true);
    };

    const handleVerification = useCallback(async () => {
        if (!executeRecaptcha) {
            toast.error(t('welcome.captchaError') || 'reCAPTCHA not loaded. Please refresh the page.');
            return;
        }

        try {
            setIsVerifying(true);

            // Execute reCAPTCHA v3
            const token = await executeRecaptcha('contact_email');

            console.log('reCAPTCHA v3 token obtained:', token.substring(0, 20) + '...');

            // For now, just open email client (in production, verify token on backend)
            window.location.href = `mailto:${contactEmail}?subject=UFlow - Question about the study`;
            toast.success(t('welcome.emailVerified') || 'Verification successful! Opening email client...');
            setShowEmailForm(false);

        } catch (error) {
            console.error('reCAPTCHA error:', error);
            toast.error(t('welcome.captchaError') || 'Verification failed. Please try again.');
        } finally {
            setIsVerifying(false);
        }
    }, [executeRecaptcha, contactEmail, t]);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 50%, #90CAF9 100%)',
                py: 6
            }}
        >
            <Container maxWidth="lg">
                <Fade in timeout={800}>
                    <Box>
                        {/* Welcome Header */}
                        <Box sx={{ textAlign: 'center', mb: 6 }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 800,
                                    color: '#1976D2',
                                    mb: 2,
                                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                                    textShadow: '0 2px 8px rgba(25, 118, 210, 0.1)'
                                }}
                            >
                                {t('welcome.title') || 'Welcome to UFlow!'}
                            </Typography>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#42A5F5',
                                    fontWeight: 500,
                                    fontSize: { xs: '1.1rem', md: '1.5rem' }
                                }}
                            >
                                {t('welcome.subtitle') || 'Your journey to better productivity starts here'}
                            </Typography>
                        </Box>

                        {/* Main Content Cards */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 4 }}>
                            {/* What is UFlow Card */}
                            <Grow in timeout={1200}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        background: 'white',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        border: '2px solid #E3F2FD',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 28px rgba(25, 118, 210, 0.15)',
                                            borderColor: '#90CAF9'
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            background: 'linear-gradient(135deg, #42A5F5 0%, #2196F3 100%)',
                                            p: 2.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5
                                        }}
                                    >
                                        <InfoOutlinedIcon sx={{ fontSize: 32, color: 'white' }} />
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                                            {t('welcome.whatIsUFlow') || 'What is UFlow?'}
                                        </Typography>
                                    </Box>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="body1" sx={{ color: '#424242', lineHeight: 1.8, mb: 2 }}>
                                            {t('welcome.description1') || 'Take our daily survey to understand your chronotype and optimize your productivity. Track your energy levels throughout the day and receive personalized recommendations.'}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 2,
                                                borderRadius: 2,
                                                background: '#E3F2FD'
                                            }}
                                        >
                                            <CheckCircleOutlineIcon sx={{ color: '#1976D2', fontSize: 24 }} />
                                            <Typography variant="body2" sx={{ color: '#1976D2', fontWeight: 600 }}>
                                                {t('welcome.description2') || 'A typical observation period will be 14 days'}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grow>

                            {/* Next Steps Card */}
                            <Grow in timeout={1400}>
                                <Card
                                    elevation={0}
                                    sx={{
                                        background: 'white',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s ease',
                                        border: '2px solid #E3F2FD',
                                        '&:hover': {
                                            transform: 'translateY(-8px)',
                                            boxShadow: '0 12px 28px rgba(25, 118, 210, 0.15)',
                                            borderColor: '#90CAF9'
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            background: 'linear-gradient(135deg, #64B5F6 0%, #42A5F5 100%)',
                                            p: 2.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5
                                        }}
                                    >
                                        <CheckCircleOutlineIcon sx={{ fontSize: 32, color: 'white' }} />
                                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                                            {t('welcome.nextSteps') || 'What happens next?'}
                                        </Typography>
                                    </Box>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="body1" sx={{ color: '#424242', lineHeight: 1.8, mb: 2 }}>
                                            {t('welcome.contactInfo') || 'Our research team will contact you shortly to explain the details of the study and answer any questions you may have.'}
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: '#424242', lineHeight: 1.8 }}>
                                            {t('welcome.groupAssignment') || 'You will receive an email with instructions on how to proceed with the study. Please check your email regularly for important updates.'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grow>
                        </Box>

                        {/* Contact Section */}
                        <Grow in timeout={1600}>
                            <Card
                                elevation={0}
                                sx={{
                                    background: 'white',
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    mb: 4,
                                    border: '2px solid #E3F2FD'
                                }}
                            >
                                <Box
                                    sx={{
                                        background: 'linear-gradient(135deg, #81D4FA 0%, #4FC3F7 100%)',
                                        p: 2.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5
                                    }}
                                >
                                    <HelpOutlineIcon sx={{ fontSize: 32, color: 'white' }} />
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
                                        {t('welcome.needHelp') || 'Need Help?'}
                                    </Typography>
                                </Box>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="body1" sx={{ color: '#424242', lineHeight: 1.8, mb: 3 }}>
                                        {t('welcome.helpText') || 'If you have any questions or concerns, or if you did not receive instructions after a few days, please contact us:'}
                                    </Typography>

                                    {!showEmailForm ? (
                                        <Button
                                            variant="contained"
                                            size="large"
                                            startIcon={<EmailIcon />}
                                            onClick={handleEmailClick}
                                            sx={{
                                                background: 'linear-gradient(135deg, #42A5F5 0%, #2196F3 100%)',
                                                color: 'white',
                                                textTransform: 'none',
                                                fontSize: '1rem',
                                                py: 1.5,
                                                px: 4,
                                                borderRadius: 3,
                                                boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                                                    boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)',
                                                    transform: 'translateY(-2px)'
                                                },
                                                transition: 'all 0.3s ease'
                                            }}
                                        >
                                            {t('welcome.contactButton') || 'Contact Us'}
                                        </Button>
                                    ) : (
                                        <Box>
                                            <Typography variant="body2" sx={{ mb: 3, color: '#616161', fontWeight: 500 }}>
                                                {t('welcome.verificationPrompt') || 'Click the button below to contact us. reCAPTCHA v3 will verify you automatically.'}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                <Button
                                                    variant="contained"
                                                    size="large"
                                                    onClick={handleVerification}
                                                    disabled={isVerifying}
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #42A5F5 0%, #2196F3 100%)',
                                                        color: 'white',
                                                        textTransform: 'none',
                                                        py: 1.5,
                                                        px: 4,
                                                        borderRadius: 3,
                                                        boxShadow: '0 4px 15px rgba(33, 150, 243, 0.3)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
                                                            boxShadow: '0 6px 20px rgba(33, 150, 243, 0.4)'
                                                        },
                                                        '&:disabled': {
                                                            background: '#BDBDBD',
                                                            color: 'white'
                                                        },
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    {isVerifying ? t('welcome.verifying') || 'Verifying...' : t('welcome.verify') || 'Verify & Contact'}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="large"
                                                    onClick={() => {
                                                        setShowEmailForm(false);
                                                    }}
                                                    sx={{
                                                        borderColor: '#90CAF9',
                                                        color: '#42A5F5',
                                                        textTransform: 'none',
                                                        py: 1.5,
                                                        px: 4,
                                                        borderRadius: 3,
                                                        borderWidth: 2,
                                                        '&:hover': {
                                                            borderColor: '#42A5F5',
                                                            borderWidth: 2,
                                                            background: '#E3F2FD'
                                                        }
                                                    }}
                                                >
                                                    {t('welcome.cancel') || 'Cancel'}
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grow>

                    </Box>
                </Fade>
            </Container>
        </Box>
    );
};

export default WelcomeAfterRegistration;
